import api from '../../helpers/api';
import { IUnitType, UNIT_STATE_IN_BAG, UNIT_STATE_IN_HAND } from '../Unit/Unit-types';
import Router from '../../router';
import { IReplayState, ISnapshotResponse } from './Replay-types';
import { store } from '../../main';
import { IActionLogPayload } from '../ActionsLog/ActionsLog-types';
import { IDecree } from '../DecreeCard/DecreeCard-types';

const replayState: IReplayState = {
    state: {
        actionsLog: [],
        activePlayerId: null,
        creatorId: null,
        decrees: [],
        draftingPlayerId: null,
        forts: [],
        loaded: false,
        mastery: false,
        numOfPlayers: null,
        passwordRequired: false,
        player: null,
        players: [],
        playing: false,
        roomName: '',
        snapshotCount: 0,
        snapshotIndex: 0,
        snapshots: [],
        state: null,
        teams: [],
        turnOrder: [],
        unitTypes: [],
        unitsInBag: [],
        unitsInHand: [],
        userId: null,
        winningTeamId: null,
        winningScore: 6,
        poisonTokens: [],
        twoPlayersFullBoard: false,
        theme: null,
    },
    mutations: {
        setReplayDecrees(state, decrees: IDecree[]) {
            state.decrees = decrees;
        },
        setReplayGameState(state, payload: { snapshot: ISnapshotResponse }) {
            const game = payload.snapshot;
            state.activePlayerId = game.activePlayerId;
            state.draftingPlayerId = game.draftingPlayerId;
            state.numOfPlayers = game.numOfPlayers;
            state.players = game.players;
            state.roomName = game.roomName;
            state.state = game.state;
            state.teams = game.teams;
            state.winningScore = game.winningScore;
            state.winningTeamId = game.winningTeamId;
            state.passwordRequired = Boolean(game.passwordRequired);
            state.loaded = true;
            state.turnOrder = game.turnOrder;
            state.creatorId = game.creator.uuid;
            state.forts = game.forts;
            state.poisonTokens = game.poisonTokens;

            const player = state.players.filter(playerInGame => playerInGame.user.id === state.userId)[0];

            if (player) {
                state.player = player;
            } else {
                state.player = state.players[0];
            }

            state.unitsInHand = state.player.units.filter(unit => unit.state === UNIT_STATE_IN_HAND);
            state.unitsInBag = state.player.units.filter(unit => unit.state === UNIT_STATE_IN_BAG);

            if (game.settings) {
                state.settingsUpdatedAt = game.settings.updatedAt;
                state.twoPlayersFullBoard = game.settings.twoPlayersFullBoard;
                state.mastery = game.settings.mastery;
                state.shock = game.settings.shock;
                state.blitz = game.settings.blitz;
                state.altFootman = game.settings.altFootman;
                state.altLancer = game.settings.altLancer;
                state.altMarshal = game.settings.altMarshal;
                state.theme = game.settings.theme;
            }
        },
        setReplayUnitTypes(state, unitTypes: IUnitType[]) {
            state.unitTypes = unitTypes;
        },
        setSnapshots(state, snapshots: ISnapshotResponse[]) {
            state.snapshots = snapshots;
        },
        setSnapshotIndex(state, snapshotIndex: number) {
            state.snapshotIndex = snapshotIndex;
        },
        setSnapshotActionsLog(state, actionsLog: IActionLogPayload[]) {
            state.actionsLog = actionsLog;
        },
        setSnapshotCount(state, snapshotCount: number) {
            state.snapshotCount = snapshotCount;
        },
        setPlayingState(state, playing: boolean) {
            state.playing = playing;
        },
        setUserId(state, userId: number) {
            state.userId = userId;
        },
        setLoaded(state, value: boolean) {
            state.loaded = value;
        },
    },
    actions: {
        async getReplayDecrees({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/decrees`, 'GET');

            if (response.ok) {
                const decrees: IDecree[] = await response.json();
                commit('setReplayDecrees', decrees);
            }
        },
        async getSnapshots({ commit }, gameId: number) {
            let snapshots = [];
            let response;

            const metadata = await api.request(`/game/${gameId}/snapshots/meta`);

            if (metadata.status === 401) {
                Router.push({ name: 'login', params: { redirect: `/game/${gameId}/replay` } });
            } else if (metadata.status === 400) {
                Router.push('/matches');
            } else if (metadata.status === 404) {
                Router.push('/rooms');
            }

            const { total, limit } = (await metadata.json());
            const pages = Math.ceil(total / limit);

            for (let page = 1; page <= pages; page += 1) {
                response = await api.request(`/game/${gameId}/snapshots/${page}`);

                if (response.ok) {
                    snapshots = snapshots.concat(await response.json());
                } else if (response.status === 401) {
                    Router.push({ name: 'login', params: { redirect: `/game/${gameId}/replay` } });
                } else if (response.status === 400) {
                    Router.push('/matches');
                } else if (response.status === 404) {
                    Router.push('/rooms');
                }
            }

            if (!snapshots.length) {
                throw new Error('Cannot load replay since no moves have been made');
            }

            const userId = store.state.loginState.userId;
            commit('setPlayingState', false);
            commit('setSnapshots', snapshots);
            commit('setSnapshotIndex', 0);
            commit('setSnapshotCount', snapshots.length);
            commit('setUserId', userId);
            commit('setReplayGameState', { snapshot: snapshots[0] });
        },
        async getReplayUnitTypes({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/unitTypes`, 'GET');
            const unitTypes: IUnitType[] = await response.json();

            if (response.ok) {
                commit('setReplayUnitTypes', unitTypes);
            }
        },
        setReplayUnitTypes({ commit }, unitTypes: IUnitType[]) {
            commit('setReplayUnitTypes', unitTypes);
        },
        setSnapshotIndex({ commit }, snapshotIndex: number) {
            commit('setSnapshotIndex', snapshotIndex);
        },
        setReplayGameState({ commit }, gameState: ISnapshotResponse) {
            commit('setReplayGameState', gameState);
        },
        async getSnapshotActionsLog({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/log`);
            const actionsLog: IActionLogPayload[] = await response.json();

            if (response.ok) {
                commit('setSnapshotActionsLog', actionsLog);
            }
        },
        setPlayingState({ commit }, playing: boolean) {
            commit('setPlayingState', playing);
        },
        setSnapshots({ commit }, snapshots: ISnapshotResponse[]) {
            commit('setSnapshots', snapshots);
        },
        setLoaded({ commit }, value: boolean) {
            commit('setLoaded', value);
        },
    },
    getters: {
        replayPlayer(state) {
            const userId = store.state.loginState.userId;

            let player = state.players.filter(playerInGame => playerInGame.user.id === userId)[0];

            if (!player) {
                player = state.players[0];
            }

            return player;
        },
    },
};

export default replayState;
